import React, { useState } from 'react'
import classNames from 'classnames'
import IconButton from '../IconButton/IconButton'
import MagnifyingGlass from '../../icons/MagnifyingGlass.svg'
import Close from '../../icons/Close.svg'
import Divider from '../../icons/Divider.svg'
import RightArrow from '../../icons/RightArrow.svg'
import { SearchProps } from './Search.types'
import css from './Search.styles.scss'

const searchSizeClassname = (size: SearchProps['size']) => {
  switch (size) {
    case 'small':
      return css.search__small
    case 'medium':
      return css.search__medium
    case 'large':
      return css.search__large
    default:
      return css.search__medium
  }
}

const searchVariantClassname = (variant: SearchProps['variant']) => {
  switch (variant) {
    case 'standard':
      return css.search__standard
    case 'inverted':
      return css.search__inverted
    default:
      return css.search__standard
  }
}

const Search: React.FC<SearchProps> = ({
  size,
  variant,
  error,
  defaultText,
  helperText,
  onSubmit,
  useTrailingIcon,
  renderTrailingIcon,
  useSecondaryTrailingIcon,
  renderSecondaryTrailingIcon,
  className,
}) => {
  const [inputValue, setInputValue] = useState('')

  const SearchClasses = classNames(
    css.search,
    className,
    searchSizeClassname(size),
    error ? css.search__error : searchVariantClassname(variant)
  )

  // Default buttons to render, if a render function is not provided
  const renderCloseButton = () => (
    <IconButton
      aria-label="Clear"
      data-testid="clear-icon"
      size="sm"
      variant="standard-default"
      onClick={() => setInputValue('')}
    >
      <Close />
    </IconButton>
  )

  const renderSubmitButton = () => (
    <IconButton
      aria-label="Submit"
      data-testid="submit-icon"
      size="sm"
      variant="standard-default"
      onClick={() => onSubmit(inputValue)}
    >
      <RightArrow />
    </IconButton>
  )

  const trailingIcon = renderTrailingIcon
    ? renderTrailingIcon(inputValue, setInputValue)
    : renderCloseButton()
  const secondaryTrailingIcon = renderSecondaryTrailingIcon
    ? renderSecondaryTrailingIcon(inputValue, setInputValue)
    : renderSubmitButton()

  const trailingIconElem = (
    <div className={css.search__trailingIconContainer}>
      {useTrailingIcon && trailingIcon}
      {useSecondaryTrailingIcon && <Divider />}
      {useSecondaryTrailingIcon && secondaryTrailingIcon}
    </div>
  )

  return (
    <div className={SearchClasses}>
      <div className={css.search__searchInputContainer}>
        <MagnifyingGlass className={css.search__magnifyingGlassIcon} />
        <input
          className={css.search__searchInput}
          placeholder={defaultText}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent) =>
            e.key === 'Enter' ? onSubmit(inputValue) : null
          }
        />
        {!!inputValue && useTrailingIcon && trailingIconElem}
      </div>
      {!!helperText && <p className={css.search__helperText}>{helperText}</p>}
    </div>
  )
}

export default Search
